import { absenceDayPartError, absenceDayPartQuantity } from './absenceDayPart';
import type { AbsenceTimeOffRequestCalculation, AbsenceTimeOffRequestInput } from './model';
import { fixturePublicHolidayDates } from './publicHolidayModel';
import { AbsenceRequestValidationError } from './absenceRequestValidation';

type FixtureQuantityInput = Pick<AbsenceTimeOffRequestInput, 'startDate' | 'endDate' | 'unitOfTime' | 'quantity' | 'dayPart'> & { dayCountBasis?: 'WORKDAYS' | 'CALENDAR_DAYS' };
type FixtureQuantityResult = Pick<AbsenceTimeOffRequestCalculation, 'quantity' | 'dayPart' | 'dayCountBreakdown'>;

/** 시연 전용: 월~금과 fixture에 등록된 일부 공휴일을 사용한다. 실제 정책 계산은 서버가 소유한다. */
export function fixtureAbsenceRequestQuantity(input: FixtureQuantityInput): FixtureQuantityResult {
  const start = parseFixtureDate(input.startDate);
  const end = parseFixtureDate(input.endDate);
  if (!start || !end) throw new AbsenceRequestValidationError('시작일과 종료일을 올바르게 입력해 주세요.', !start ? 'startDate' : 'endDate');
  if (end < start) throw new AbsenceRequestValidationError('종료일은 시작일과 같거나 이후여야 합니다.', 'endDate');

  if (input.unitOfTime === 'HOURS') {
    if (!input.quantity?.trim() || !Number.isFinite(Number(input.quantity)) || Number(input.quantity) <= 0) {
      throw new AbsenceRequestValidationError('사용 시간을 0보다 크게 입력해 주세요.', 'quantity');
    }
    if (input.dayPart) throw new AbsenceRequestValidationError('시간 단위 휴가에는 반차·반반차를 선택할 수 없습니다.', 'dayPart');
    return { quantity: input.quantity };
  }
  if (input.unitOfTime !== 'DAYS') throw new AbsenceRequestValidationError('휴가 단위를 확인해 주세요.', 'timeOffType');

  const partQuantity = absenceDayPartQuantity(input.dayPart);
  const dayPartError = absenceDayPartError({ ...input, quantity: partQuantity !== undefined ? String(partQuantity) : input.quantity ?? '1' });
  if (dayPartError) throw new AbsenceRequestValidationError(dayPartError, 'dayPart');

  const holidays = new Set(fixturePublicHolidayDates({ from: input.startDate, to: input.endDate }).days.map(({ date }) => date));
  let workdays = 0;
  let selectedDays = 0;
  for (const date = new Date(start); date <= end; date.setUTCDate(date.getUTCDate() + 1)) {
    selectedDays += 1;
    const weekday = date.getUTCDay();
    if (input.dayCountBasis === 'CALENDAR_DAYS' || (weekday !== 0 && weekday !== 6 && !holidays.has(date.toISOString().slice(0, 10)))) workdays += 1;
  }
  if (workdays === 0) throw new AbsenceRequestValidationError('선택한 기간에 휴가를 사용할 근무일이 없습니다.', 'startDate');
  return {
    quantity: String(partQuantity ?? workdays), dayPart: input.dayPart,
    ...(partQuantity === undefined && !input.quantity?.trim() ? {
      dayCountBreakdown: { basis: input.dayCountBasis ?? 'WORKDAYS', selectedDays, countedDays: workdays, excludedDays: selectedDays - workdays },
    } : {}),
  };
}

function parseFixtureDate(value: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value ? date : undefined;
}
